import { useEffect, useState } from "react";
import { Search, X, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUSES, AU_STATES } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";
import { jobTypeNames } from "@/lib/jobTypes";
import { truckLabel } from "@/components/settings/FleetEditor";
import { cn } from "@/lib/utils";

const ALL = "__all";

export const FilterSelect = ({ id, placeholder, value, options, onChange }) => (
  <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
    <SelectTrigger data-testid={`filter-${id}`} className={cn("h-9 w-full bg-white text-sm sm:w-auto sm:min-w-[140px]", value && "border-sky-400 bg-sky-50 text-sky-900")}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{placeholder}: All</SelectItem>
      {options.map((o) => {
        const opt = typeof o === "string" ? { value: o, label: o } : o;
        return <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>;
      })}
    </SelectContent>
  </Select>
);

export const SCOPES = [
  { value: "upcoming", label: "Upcoming" },
  { value: "past", label: "Past" },
  { value: "all", label: "All" },
];

export const EMPTY_FILTERS = { q: "", scope: "upcoming", date_from: "", date_to: "", status: "", sales_person: "", job_type: "", assigned_team: "", subbie: "", truck_assigned: "", state: "", bill_to_company: "", lead_source: "", flagged: "" };

export const BookingFilters = ({ filters, setFilters, extraLeadSources = [] }) => {
  const { settings } = useSettings();
  const [q, setQ] = useState(filters.q);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const leadOptions = (() => {
    const seen = new Set(); const out = [];
    for (const n of [...(settings.lead_sources || []), ...settings.companies.map((c) => c.name), ...extraLeadSources]) {
      const k = String(n || "").trim().toLowerCase();
      if (k && !seen.has(k)) { seen.add(k); out.push(n); }
    }
    return out;
  })();

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === q ? f : { ...f, q })), 250);
    return () => clearTimeout(t);
  }, [q, setFilters]);

  const active = Object.entries(filters).some(([k, v]) => k !== "scope" && v);

  return (
    <div className="mb-4 space-y-3 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Move Pro job #, customer, mobile, email, pickup or delivery…" data-testid="bookings-search-input" className="h-10 bg-slate-50 pl-9" />
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="filter-scope">
          {SCOPES.map((s) => (
            <button key={s.value} onClick={() => set("scope", s.value)} data-testid={`scope-${s.value}`}
              className={cn("rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors", filters.scope === s.value ? "bg-[#0E1E38] text-white" : "text-slate-600 hover:text-slate-900")}>
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
        <div className="col-span-2 flex items-center gap-1.5">
          <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" className={cn("h-9 bg-white text-sm sm:w-[150px]", filters.date_from && "border-sky-400 bg-sky-50")} />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" className={cn("h-9 bg-white text-sm sm:w-[150px]", filters.date_to && "border-sky-400 bg-sky-50")} />
        </div>
        <FilterSelect id="status" placeholder="Status" value={filters.status} options={STATUSES} onChange={(v) => set("status", v)} />
        <FilterSelect id="sales-person" placeholder="Sales person" value={filters.sales_person} options={settings.sales_people} onChange={(v) => set("sales_person", v)} />
        <FilterSelect id="job-type" placeholder="Job type" value={filters.job_type} options={jobTypeNames(settings.job_types)} onChange={(v) => set("job_type", v)} />
        <FilterSelect id="team" placeholder="Our drivers" value={filters.assigned_team} options={settings.teams} onChange={(v) => setFilters((f) => ({ ...f, assigned_team: v, subbie: v ? "" : f.subbie }))} />
        <FilterSelect id="subbie" placeholder="Subcontractor" value={filters.subbie} options={[{ value: "__any", label: "Any subcontractor" }, ...(settings.subbies || [])]} onChange={(v) => setFilters((f) => ({ ...f, subbie: v, assigned_team: v ? "" : f.assigned_team }))} />
        <FilterSelect id="lead-source" placeholder="Lead source" value={filters.lead_source} options={leadOptions} onChange={(v) => set("lead_source", v)} />
        <FilterSelect id="truck" placeholder="Truck" value={filters.truck_assigned} options={(settings.fleet || []).map(truckLabel)} onChange={(v) => set("truck_assigned", v)} />
        <FilterSelect id="state" placeholder="State" value={filters.state} options={AU_STATES} onChange={(v) => set("state", v)} />
        <FilterSelect id="company" placeholder="Bill to company" value={filters.bill_to_company} options={settings.companies.map((c) => c.name)} onChange={(v) => set("bill_to_company", v)} />
        <button type="button" onClick={() => set("flagged", filters.flagged ? "" : "1")} data-testid="filter-flagged"
          className={cn("inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors", filters.flagged ? "border-amber-400 bg-amber-50 text-amber-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}>
          <AlertTriangle className="h-3.5 w-3.5" /> Missing info
        </button>
        {active && (
          <Button variant="ghost" size="sm" onClick={() => { setQ(""); setFilters({ ...EMPTY_FILTERS, scope: filters.scope }); }} data-testid="btn-clear-filters" className="text-slate-500">
            <X className="h-4 w-4" /> Clear
          </Button>
        )}
      </div>
    </div>
  );
};
